/**
 * Currency formatting helpers.
 * All money display in the app should go through this file.
 */

// Fallback metadata if the backend hasn't returned it yet
const FALLBACK_META = {
    eur: { symbol: '€', locale: 'fr-FR', decimals: 2 },
    usd: { symbol: '$', locale: 'en-US', decimals: 2 },
    gbp: { symbol: '£', locale: 'en-GB', decimals: 2 },
    gnf: { symbol: 'FG', locale: 'fr-GN', decimals: 0 },
    xof: { symbol: 'FCFA', locale: 'fr-SN', decimals: 0 },
};

// Locales where the currency symbol is naturally placed *before* the amount.
const SYMBOL_BEFORE = new Set(['usd', 'gbp']);

/**
 * Format an amount using TalentIQ's canonical symbols (never the browser's
 * locale-picked ones). This guarantees the symbol displayed matches the one
 * used for billing (e.g. "FCFA" not "F CFA", "FG" not "GNF").
 *
 *   formatMoney(49, 'eur')        → "49,00 €"
 *   formatMoney(550000, 'gnf')    → "550 000 FG"
 *   formatMoney(32500, 'xof')     → "32 500 FCFA"
 *   formatMoney(55, 'usd')        → "$55.00"
 */
export function formatMoney(amount, currency = 'eur', metaOverride = null) {
    const code = (currency || 'eur').toLowerCase();
    const meta = metaOverride || FALLBACK_META[code] || FALLBACK_META.eur;
    const n = Number(amount || 0);
    let numberPart;
    try {
        numberPart = new Intl.NumberFormat(meta.locale, {
            style: 'decimal',
            minimumFractionDigits: meta.decimals,
            maximumFractionDigits: meta.decimals,
        }).format(n);
    } catch {
        numberPart = meta.decimals === 0 ? Math.round(n).toString() : n.toFixed(meta.decimals);
    }
    return SYMBOL_BEFORE.has(code)
        ? `${meta.symbol}${numberPart}`
        : `${numberPart} ${meta.symbol}`;
}

/**
 * Compact price form used on marketing (landing) cards:
 *   compactPrice(49, 'eur') → { amount: "49", suffix: "€" }
 * Falls back to full symbol so the JSX can style them independently.
 */
export function compactPrice(amount, currency = 'eur', metaOverride = null) {
    const code = (currency || 'eur').toLowerCase();
    const meta = metaOverride || FALLBACK_META[code] || FALLBACK_META.eur;
    const n = Number(amount || 0);
    const rounded = meta.decimals === 0 ? Math.round(n).toLocaleString(meta.locale) : n.toFixed(0);
    return { amount: rounded, suffix: meta.symbol };
}
